import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Text } from 'rsuite';
import { selectConfigExpressLocalContactVerbiage } from '../../../store/slices/config/configSelectors';
import { objectHash } from '../../../common/utils';
import { ApplicationData } from 'src/types/flow';
import { Person } from 'src/types/person';
import { PersonData } from './PersonData';
import { useUserRole } from 'src/hooks/useUserRole';
import { ROLES, ROLES_INFO } from './types';

interface PersonInfoProps {
  data: ApplicationData;
  role: ROLES;
}

interface PersonWithHash extends Person {
  hash: number;
}

export const PersonInfo = ({ data, role }: PersonInfoProps) => {
  const selectedLocalContactVerbiage: string = useSelector(
    selectConfigExpressLocalContactVerbiage
  ) as string;

  const parseRoleInfoText = (roleInfo: string) => {
    if (!roleInfo) return '';
    return roleInfo
      .split('_')
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(' ');
  };

  const foundPerson = useMemo((): {
    person: PersonWithHash[];
    roleInfo: string;
  } => {
    let roleInfo = '';
    let _person: Person | Person[];

    if (role === ROLES.PERMIT_HOLDER) {
      roleInfo = parseRoleInfoText(data.registrant?.registrantType as string);
      _person = data.registrant?.registrant as Person;
    } else if (role === ROLES.OWNER) {
      roleInfo = ROLES_INFO.OWNER;
      _person = data.registrant?.owner as Person[];
    } else if (role === ROLES.PROPERTY_MANAGER) {
      roleInfo = ROLES_INFO.PROPERTY_MANAGER;
      _person = data.contacts?.propertyManager as Person;
    } else {
      roleInfo = selectedLocalContactVerbiage;
      _person = data.contacts?.localContact as Person;
    }

    if (!_person) {
      return { person: [], roleInfo: '' };
    }

    const returnPerson = Array.isArray(_person) ? _person : [_person];

    return {
      roleInfo: roleInfo,
      person: returnPerson.map((item) => ({
        ...item,
        hash: objectHash(item),
      })),
    };
  }, [data, role, selectedLocalContactVerbiage]);

  const { isPropertyManager, isOwner, ownerIndex } = useUserRole(
    data.registrant,
    data.contacts,
    data.tenant,
    foundPerson.person[0]
  );

  const sameAsText = useMemo(() => {
    if (role !== ROLES.OWNER) {
      if (isPropertyManager && role !== ROLES.PROPERTY_MANAGER) {
        return 'Same as property manager.';
      }
      if (isOwner) {
        return `Same as owner #${ownerIndex + 1}.`;
      }
    }
    return null;
  }, [isPropertyManager, role, ownerIndex, isOwner]);

  if (!foundPerson?.person) return null;

  return (
    <>
      {sameAsText && <Text>{sameAsText}</Text>}
      {!sameAsText &&
        foundPerson.person.map((item, index) => (
          <PersonData
            personDetails={item}
            role={foundPerson.roleInfo}
            number={index + 1}
            key={String(item?.hash)}
          />
        ))}
    </>
  );
};
